import {AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {take} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';
import * as Quill from 'quill';
import {FieldType} from '@ngx-formly/core';
import {FileItem, FileUploader, ParsedResponseHeaders} from 'ng2-file-upload';
import {AuthenticationService} from '../../Services/authentication.service';
import {environment} from '../../../../environments/environment';
import {Helper} from '../../../helpers/helper.service';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

const baseUri = environment.apiUrl;

/**
 * Quill JS Custom elements
 */
const Link = Quill.import('formats/link');
const Image = Quill.import('formats/image');
const BlockEmbed = Quill.import('blots/block/embed');
const QuillLinkBlot = Quill.import('formats/link');

class BtnLinkBlot extends Link {
  static blotName = 'btn-link';
  static tagName = 'a';
  static className = 'btn';

  static create(url) {
    // If it's missing http://, https://, or //, prepend https://
    if (url && !/^https?:\/\//i.test(url) && !url.startsWith('//')) {
      url = `https://${url}`;
    }
    const node = super.create();
    node.setAttribute('href', url);
    node.setAttribute('class', 'btn btn-primary ripple light');
    return node;
  }

  static formats(node) {
    return node.getAttribute('href');
  }
}

class ImageBlot extends Image {
  static create(url) {
    const node = super.create(url);
    node.setAttribute('class', 'img-fluid');
    return node;
  }
}

class VideoBlot extends BlockEmbed {
  static blotName = 'video';
  static tagName = 'div';
  static className = 'embed-responsive';

  static create(value) {

    const node = super.create();
    node.classList.add('embed-responsive-16by9');

    const child = document.createElement('iframe');
    child.setAttribute('frameborder', '0');
    child.setAttribute('allowfullscreen', 'true');
    child.setAttribute('src', this.sanitize(value));
    child.classList.add('embed-responsive-item');

    node.appendChild(child);

    return node;
  }

  static sanitize(url) {
    return Link.sanitize(url);
  }

  static value(domNode) {
    const iframe = domNode.querySelector('iframe');
    return iframe.getAttribute('src');
  }
}

class LinkBlot extends QuillLinkBlot {
  static create(value: string) {
    // If it's missing http://, https://, or //, prepend https://
    if (value && !/^https?:\/\//i.test(value) && !value.startsWith('//')) {
      value = `https://${value}`;
    }
    return super.create(value);
  }
}

Quill.register(BtnLinkBlot, true);
Quill.register(ImageBlot, true);
Quill.register(VideoBlot, true);
Quill.register(LinkBlot, true);

const toolsMinimal = [
  [{'font': []}],
  [{'header': [1, 2, 3, 4, 5, 6, false]}],

  ['bold', 'italic', 'underline'],                  // toggled buttons

  [{'list': 'ordered'}, {'list': 'bullet'}],
  [{'script': 'sub'}, {'script': 'super'}],                   // superscript/subscript
  [{'indent': '-1'}, {'indent': '+1'}],                       // outdent/indent
  [{'direction': 'rtl'}],                                     // text direction


  [{'color': []}, {'background': []}],                        // dropdown with defaults from theme
  [{'align': []}],
];

const toolsNormal = [
  [{'font': []}],
  [{'header': [1, 2, 3, 4, 5, 6, false]}],

  ['bold', 'italic', 'underline', 'strike'],                  // toggled buttons
  ['blockquote', 'code-block'],

  [{'list': 'ordered'}, {'list': 'bullet'}],
  [{'script': 'sub'}, {'script': 'super'}],                   // superscript/subscript
  [{'indent': '-1'}, {'indent': '+1'}],                       // outdent/indent
  [{'direction': 'rtl'}],                                     // text direction

  [{'color': []}, {'background': []}],                        // dropdown with defaults from theme
  [{'align': []}],

  ['clean'],                                                  // remove formatting button
];

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EditorComponent),
      multi: true
    }
  ]
})
export class EditorComponent implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor, Validator {
  @ViewChild('inputModal', {static: false}) inputModal;
  @ViewChild('uploadEl', {static: false}) uploadElRef: ElementRef;
  @ViewChild('imageElement', {static: false}) imageElement: ElementRef;

  public quill;
  public modules = {};
  public input_address: string;
  public inputAccepted = new EventEmitter<string>();
  public modalHandler = new BehaviorSubject<string>(null);
  @Input() label: string;
  @Input() scope: 'backoffice' | 'dealer-system' = 'backoffice';
  @Input() bupload: string = '';
  @Input() toolsType: 'minimal' | 'normal' = 'normal';
  @Input() extraTools = [];
  @Input() required = false;
  @Input() ngModel = '';
  @Output() ngModelChange: EventEmitter<string> = new EventEmitter();

  /**
   * Upload file variables
   */
  public fileUrl = null;

  public uploader: FileUploader = new FileUploader({
    itemAlias: 'content',
    url: baseUri + '/' + this.scope + '/file_manager?bupload=' + this.bupload,
    authToken: this.auth.getBearerHeader(),
  });
  private target: any;
  public addFileItem = false;
  public isUploading = false;
  public isNotAdd = false;

  private onValidatorChangeFn: () => void = () => {};

  constructor(
    private helper: Helper,
    private auth: AuthenticationService,
    private elem: ElementRef,
  ) {
  }

  writeValue(value: string): void {
    this.ngModel = value;
    this.onValidatorChangeFn();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.required) {
      return null;
    }
    const v = control.value;
    if (v == null || v === '') {
      return {required: true};
    }
    const text = String(v).replace(/<[^>]+>/g, '').replace(/\u00a0/g, ' ').trim();
    if (!text) {
      return {required: true};
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChangeFn = fn;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required']) {
      this.onValidatorChangeFn();
    }
  }

  registerOnChange(fn: Function): void {
    this.ngModelChange.subscribe(v => {
      fn(v);
    });
  }

  registerOnTouched(fn: Function): void {
    this.ngModelChange.subscribe(v => {
      fn(v);
    });
  }

  setDisabledState?(isDisabled: boolean): void {
  }

  ngOnInit() {
    this.uploader.onAfterAddingFile = (item => {
      if (this.target) {
        this.target.value = '';
      }
      item.withCredentials = false;
    });

    this.modules = {
      toolbar: {
        container: [
          ...(this.toolsType === 'minimal' ? toolsMinimal : []),
          ...(this.toolsType === 'normal' ? toolsNormal : []),

          this.extraTools

        ],
        handlers: {
          'btn-link': (value) => {
            if (value) {
              const range = this.quill.getSelection();
              if (range == null || range.length === 0) {
                return;
              }
              let preview = this.quill.getText(range);
              if (/^\S+@\S+\.\S+$/.test(preview) && preview.indexOf('mailto:') !== 0) {
                preview = 'mailto:' + preview;
              }
              this.modalHandler.next('link');
              this.input_address = preview;
              this.inputAccepted.pipe(take(1)).subscribe((inputAccepted) => {
                if ('link' === inputAccepted) {
                  this.quill.format('btn-link', this.input_address);
                }
              });
            } else {
              this.quill.format('btn-link', false);
            }
          },
          'image': (value) => {
            if (value) {
              const range = this.quill.getSelection(true);
              this.modalHandler.next('image');
              this.inputAccepted.pipe(take(1)).subscribe((inputAccepted) => {
                if ('image' === inputAccepted) {
                  this.quill.insertEmbed(range.index, 'image', this.input_address, 'user');
                  this.quill.setSelection(range.index + 1, 'silent');
                }
              });
            } else {
              this.quill.format('image', false);
            }
          },
          'video': (value) => {
            if (value) {
              const range = this.quill.getSelection(true);
              this.modalHandler.next('video');
              this.inputAccepted.pipe(take(1)).subscribe((inputAccepted) => {
                if ('video' === inputAccepted) {
                  const youtubeUrlRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/;
                  const match = this.input_address.match(youtubeUrlRegex);
                  if (youtubeUrlRegex.test(this.input_address)) {
                    this.quill.insertEmbed(range.index, 'video', `https://www.youtube.com/embed/${match[2]}?rel=0`, 'user');
                  } else {
                    this.quill.insertEmbed(range.index, 'video', this.input_address, 'user');
                  }
                  this.quill.formatText(range.index + 1, 1, {height: '170', width: '400'});
                  this.quill.setSelection(range.index + 1, 'silent');
                }
              });
            } else {
              this.quill.format('video', false);
            }
          },
          'file': (value) => {
            if (value) {
              const range = this.quill.getSelection();
              if (range == null || range.length === 0) {
                return;
              }
              this.modalHandler.next('file');
              this.inputAccepted.pipe(take(1)).subscribe((inputAccepted) => {
                if ('file' === inputAccepted) {
                  this.quill.format('link', this.input_address);
                }
              });
            } else {
              this.quill.format('link', false);
            }
          },
        }
      },
    };

    this.modalHandler.subscribe((type) => {
      this.input_address = null;
      this.removeImage();
      if (null !== type) {
        this.inputModal.show();
      }
    });
  }


  ngAfterViewInit() {
    if (this.elem.nativeElement.querySelector('.ql-btn-link')) {
      this.elem.nativeElement.querySelector('.ql-btn-link').innerHTML
        = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 393 393"><defs/><path d="M130 196c0 9 7 15 15 15h102a15 15 0 000-30H145c-8 0-15 7-15 15z"/><path d="M177 237h-51c-11 0-19-9-19-20v-41c0-11 8-20 19-20h51a15 15 0 000-30h-51c-27 0-49 22-49 50v41c0 27 22 50 49 50h51a15 15 0 000-30zM266 126h-50a15 15 0 000 30h50c11 0 20 9 20 20v41c0 11-9 20-20 20h-50a15 15 0 000 30h50c28 0 50-23 50-50v-41c0-28-22-50-50-50z"/><path d="M311 1H82C37 1 0 38 0 83v227c0 45 37 82 82 82h229c45 0 82-37 82-82V83c0-45-37-82-82-82zm52 309c0 28-24 52-52 52H82c-29 0-52-24-52-52V83c0-29 23-52 52-52h229c28 0 52 23 52 52v227z"/></svg>' + this.elem.nativeElement.querySelector('.ql-btn-link').innerHTML;
    }

    if (this.elem.nativeElement.querySelector('.ql-file')) {
      this.elem.nativeElement.querySelector('.ql-file').innerHTML
        = '<i class="fa fa-file"></i>' + this.elem.nativeElement.querySelector('.ql-file').innerHTML;
    }
  }

  removeImage() {
    this.isUploading = false;
    this.fileUrl = null;
    this.uploader.clearQueue();
    if (this.uploadElRef) {
      this.uploadElRef.nativeElement.value = '';
    }
    this.input_address = null;
  }

  onEditorCreated(quill) {
    this.quill = quill;
  }

  cancelInput() {
    this.modalHandler.next(null);
    this.inputModal.hide();
  }

  fileChange() {
    const reader = new FileReader();
    reader.onloadend = (e) => {
      this.fileUrl = reader.result;
    };
    if (this.uploader.queue[0]._file) {
      return reader.readAsDataURL(this.uploader.queue[0]._file);
    }
  }

  uploadImage() {
    this.uploadFile(() => {
      this.input_address = JSON.parse(JSON.stringify(this.fileUrl));
    });
  }

  uploadFile(cb) {
    this.addFileItem = true;
    this.isUploading = true;
    this.uploader.setOptions({
      ...this.uploader.options,
      url: baseUri + '/' + this.scope + '/file_manager?bupload=' + this.bupload +
        (this.modalHandler.value === 'image' ? '&quality=75' : '')
    });
    this.uploader.uploadAll();
    this.uploader.onErrorItem = (item: FileItem, response, status: number, headers: ParsedResponseHeaders) => {
      this.removeImage();
      const res = JSON.parse(response);
      this.helper.popMessageDialog(res.title, res.errors);
    };
    this.uploader.onSuccessItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
      if (response) {
        this.addFileItem = false;
        this.isUploading = false;
        this.isNotAdd = false;
        const res = JSON.parse(response);
        this.fileUrl = environment.absolutePath + '/' + res.fullPath;
        cb();
      }
    };
  }

  acceptInput() {
    this.inputAccepted.emit(this.modalHandler.value);
    this.inputModal.hide();
  }
}
